import _get from 'lodash.get';
import _cloneDeep from 'lodash.clonedeep';
import { Typography, TypographyProps } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  billingStatusStyles as _billingStatusStyles,
  invoiceStatusStyles as _invoiceStatusStyles,
  orderStatusStyles as _orderStatusStyles,
  drayageStatusStyles as _drayageStatusStyles,
  truckingJobStyles as _truckingJobStyles,
  receiptStatusStyles as _receiptStatusStyles,
  moveableUnitStatusStyles as _moveableUnitStatusStyles,
  appointmentStatusStyles as _appointmentStatusStyles,
  locationsInventoryStatusStyles as _locationsInventoryStatusStyles,
  warehouseLocationTypeStatusStyles as _warehouseLocationTypeStatusStyles,
  inventoryTransferStatusStyles as _inventoryTransferStatusStyles,
} from '../../../styles/sharedStyles';
import { AlignType, StatusStylesType } from '../../../types';
import { stableObject } from '../../../lib';

// const orderStatusStyles = { ..._orderStatusStyles };
const billingStatusStyles = _cloneDeep(_billingStatusStyles);
const invoiceStatusStyles = _cloneDeep(_invoiceStatusStyles);
const orderStatusStyles = _cloneDeep(_orderStatusStyles);
const drayageStatusStyles = _cloneDeep(_drayageStatusStyles);
const truckingJobStyles = _cloneDeep(_truckingJobStyles);
const receiptStatusStyles = _cloneDeep(_receiptStatusStyles);
const moveableUnitStatusStyles = _cloneDeep(_moveableUnitStatusStyles);
const appointmentStatusStyles = _cloneDeep(_appointmentStatusStyles);
const locationsInventoryStatusStyles = _cloneDeep(
  _locationsInventoryStatusStyles,
);
const warehouseLocationTypeStatusStyles = _cloneDeep(
  _warehouseLocationTypeStatusStyles,
);
const inventoryTransferStatusStyles = _cloneDeep(
  _inventoryTransferStatusStyles,
);

interface Props<TRecord extends Record<string, any> = Record<string, any>> {
  typographyProps?: TypographyProps;
  record?: TRecord;
  className?: string;
  headerClassName?: string;
  cellStyle?: React.CSSProperties;
  source?: string;
  label?: string;
  columnKey?: string;
  hiddenByDefault?: boolean;
  disablePadding?: string;
  align?: AlignType;
  sortBy?: string;
  sortable?: false;
  statusClassName?: string;
  statusClassField?: string;
  stylesType?: StatusStylesType;
  variant?: 'bar' | 'dot';
  useCustomValue?: boolean;
  value?: string;
  size?: 'small' | 'medium';
  setClassOverride?: (record: TRecord) => string;
}

// Create style hooks at module level to avoid recreating on every render
const useOrderStylesHook = makeStyles({ name: 'order-Styles' })((theme) => ({
  ...orderStatusStyles,
}));

const useDrayageStylesHook = makeStyles({ name: 'drayage-Styles' })((theme) => ({
  ...drayageStatusStyles,
}));

const useInvoiceStylesHook = makeStyles({ name: 'invoice-Styles' })((theme) => ({
  ...invoiceStatusStyles,
}));

const useBillingStylesHook = makeStyles({ name: 'billing-Styles' })((theme) => ({
  ...billingStatusStyles,
}));

const useTruckingJobStylesHook = makeStyles({ name: 'trucking-job-Styles' })(
  (theme) => ({
    ...truckingJobStyles,
  }),
);

const useReceiptStatusStylesHook = makeStyles({ name: 'receipt-Styles' })(
  (theme) => ({
    ...receiptStatusStyles,
  }),
);

const useMoveableUnitStatusStylesHook = makeStyles({ name: 'mu-Styles' })(
  (theme) => ({
    ...moveableUnitStatusStyles,
  }),
);

const useAppointmentStatusStylesHook = makeStyles({ name: 'appointment-Styles' })(
  (theme) => ({
    ...appointmentStatusStyles,
  }),
);

const useLocationsInventoryStatusStylesHook = makeStyles({
  name: 'locations-Styles',
})((theme) => ({
  ...locationsInventoryStatusStyles,
}));

const useWarehouseLocationTypeStatusStylesHook = makeStyles({
  name: 'warehouseLocationType-Styles',
})((theme) => ({
  ...warehouseLocationTypeStatusStyles,
}));

const useInventoryTransferStatusStylesHook = makeStyles({
  name: 'inventory-transfer-Styles',
})((theme) => ({
  ...inventoryTransferStatusStyles,
}));

export const StatusField = <
  TRecord extends Record<string, any> = Record<string, any>,
>({
  className,
  statusClassName,
  statusClassField,
  typographyProps: _typographyProps,
  variant = 'bar',
  source,
  record: _record,
  stylesType = 'order',
  useCustomValue = false,
  value: _value,
  size = 'medium',
  setClassOverride,
  ...rest
}: Props<TRecord>) => {
  const { classes, cx } = useStyles({ size });
  const { classes: orderClasses } = useOrderStylesHook();
  const { classes: drayageClasses } = useDrayageStylesHook();
  const { classes: invoiceClasses } = useInvoiceStylesHook();
  const { classes: billingClasses } = useBillingStylesHook();
  const { classes: truckingJobClasses } = useTruckingJobStylesHook();
  const { classes: receiptClasses } = useReceiptStatusStylesHook();
  const { classes: moveableUnitClasses } = useMoveableUnitStatusStylesHook();
  const { classes: appointmentClasses } = useAppointmentStatusStylesHook();
  const { classes: locationsInventoryClasses } =
    useLocationsInventoryStatusStylesHook();
  const { classes: warehouseLocationTypeClasses } =
    useWarehouseLocationTypeStatusStylesHook();

  const { classes: inventoryTransferClasses } =
    useInventoryTransferStatusStylesHook();
  const record = _record || (stableObject as TRecord);
  const styles = [
    { [orderClasses[statusClassName ?? '']]: stylesType === 'order' },
    {
      [orderClasses[record[statusClassField ?? '']]]: stylesType === 'order',
    },
    { [drayageClasses[statusClassName ?? '']]: stylesType === 'drayage' },
    {
      [drayageClasses[record[statusClassField ?? '']]]:
        stylesType === 'drayage',
    },
    { [invoiceClasses[statusClassName ?? '']]: stylesType === 'invoice' },
    {
      [invoiceClasses[record[statusClassField ?? '']]]:
        stylesType === 'invoice',
    },
    { [billingClasses[statusClassName ?? '']]: stylesType === 'billing' },
    {
      [billingClasses[record[statusClassField ?? '']]]:
        stylesType === 'billing',
    },
    {
      [truckingJobClasses[statusClassName ?? '']]: stylesType === 'truckingJob',
    },
    {
      [truckingJobClasses[record[statusClassField ?? '']]]:
        stylesType === 'truckingJob',
    },
    { [receiptClasses[statusClassName ?? '']]: stylesType === 'receipt' },
    {
      [receiptClasses[record[statusClassField ?? '']]]:
        stylesType === 'receipt',
    },
    {
      [moveableUnitClasses[statusClassName ?? '']]:
        stylesType === 'moveableUnit',
    },
    {
      [moveableUnitClasses[record[statusClassField ?? '']]]:
        stylesType === 'moveableUnit',
    },
    {
      [appointmentClasses[statusClassName ?? '']]: stylesType === 'appointment',
    },
    {
      [appointmentClasses[record[statusClassField ?? '']]]:
        stylesType === 'appointment',
    },
    {
      [locationsInventoryClasses[statusClassName ?? '']]:
        stylesType === 'locationsInventory',
    },
    {
      [locationsInventoryClasses[record[statusClassField ?? '']]]:
        stylesType === 'locationsInventory',
    },
    {
      [warehouseLocationTypeClasses[
        statusClassField ? record[statusClassField ?? ''] : _value
      ]]: stylesType === 'warehouseLocationType',
    },
    {
      [warehouseLocationTypeClasses[
        statusClassField ? record[statusClassField ?? ''] : _value
      ]]: stylesType === 'warehouseLocationType',
    },
    {
      [inventoryTransferClasses[statusClassName ?? '']]:
        stylesType === 'inventoryTransfer',
    },
    {
      [inventoryTransferClasses[record[statusClassField ?? '']]]:
        stylesType === 'inventoryTransfer',
    },
  ];
  const typographyProps = _typographyProps || stableObject;

  const value = useCustomValue ? _value : _get(record, source as string);
  const classOverride = setClassOverride?.(record);

  return value ? (
    <>
      {variant === 'dot' ? (
        <span className={cx(classes.badgeRoot, classOverride)}>
          <Typography
            component='span'
            className={cx(classes.badge, className, ...styles)}
            {...typographyProps}
          />
          <span style={{ paddingLeft: 8 }}>{value}</span>
        </span>
      ) : (
        <span className={cx(classes.root, ...styles, classOverride)}>
          <Typography
            component='span'
            className={cx(classes.field, className)}
            {...typographyProps}
          >
            {value}
          </Typography>
        </span>
      )}
    </>
  ) : null;
};

const useStyles = makeStyles<{ size?: string }>({ name: { StatusField } })(
  (theme, { size }) => ({
    field: {
      whiteSpace: 'nowrap',
      fontSize: '12px !important',
    },
    root: {
      padding: size === 'small' ? '2.5px 6px' : '5px 12px',
      borderRadius: 3,
      fontSize: '12px !important',
      fontWeight: 600,
      letterSpacing: 0.86,
    },
    badgeRoot: {
      position: 'relative',
    },
    badge: {
      position: 'absolute',
      transition: 'transform 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
      height: 6,
      padding: 0,
      minWidth: 6,
      borderRadius: 4,
      top: '50%',
      left: '-13px',
      transform: 'scale(1) translate(50%, -50%)',
      transformOrigin: '100% 0%',
    },
  }),
);

// Hooks are now defined above the component
